/**
 * #1448 D2 (owner, 2026-10-30) — the SESSION-credential dialog (the keybook icon, editing a bound credential) answers no
 * question, so it has no turn to carry a one-time code to: a code typed there would be dropped (or, typed into a plain
 * field, saved into the key). It shows NO `prompt` field — only a credential card answering a question does. The
 * one-time fields come from the row's field types: the call's own input while the tab saw it, else the server's answer
 * in the session listing (`promptFields`, from the call's card — #1448 D4).
 *
 * ONE helper for both chat surfaces (ChatPanel, HeroChat), which used to carry the same field-type guess twice.
 */
import type { CredRequest } from './useSessionCredentials.js'

type DialogField = { name: string; type: 'password' | 'text' }

/** The row's mapping without its one-time fields — what the dialog shows and announces, and the popover lists. */
export function sessionDialogEnvMapping(cred: Pick<CredRequest, 'envMapping'> & { fieldTypes?: Readonly<Record<string, string>> }): Record<string, string> {
  const types: Readonly<Record<string, string>> = cred.fieldTypes ?? {}
  return Object.fromEntries(Object.entries(cred.envMapping).filter(([name]) => types[name] !== 'prompt'))
}

/** The dialog's fields for that mapping: a secret-looking name is a password field, else text (never `prompt`). */
export function sessionDialogFields(envMapping: Record<string, string>): DialogField[] {
  return Object.keys(envMapping).map((name) => {
    const n = name.toLowerCase()
    return { name, type: n.includes('password') || n.includes('key') || n.includes('token') || n.includes('secret') ? 'password' : 'text' }
  })
}

/** A row the dialog can open: one with a field the key fills. A row made only of one-time fields (an older "Do this
 *  later" on a codes-only card) has nothing to fill — the popover leaves it out and the key icon's dot never counts it. */
export function isDialogOpenable(cred: Pick<CredRequest, 'envMapping'> & { fieldTypes?: Readonly<Record<string, string>> }): boolean {
  return Object.keys(sessionDialogEnvMapping(cred)).length > 0
}

/** The key icon's yellow dot: some pending row the dialog can open. */
export function hasOpenablePending(credentials: ReadonlyArray<Pick<CredRequest, 'envMapping' | 'status'> & { fieldTypes?: Readonly<Record<string, string>> }>): boolean {
  return credentials.some((c) => c.status === 'pending' && isDialogOpenable(c))
}
